import type { Canvas, FabricObject } from 'fabric';

type TFabricSceneQueryServiceParams = {
    canvas: Canvas;
};

// Read-only reflection over the fabric canvas state. Every method is
// pure lookup — no mutation, no event dispatch. Consumers use these
// via the I2DEngine port when they need "what is selected", "where
// is the viewport", etc.
class FabricSceneQueryService {
    private readonly _canvas: Canvas;

    constructor(params: TFabricSceneQueryServiceParams) {
        this._canvas = params.canvas;
    }

    getActiveEntityId(): string | undefined {
        const active = this._canvas.getActiveObject();
        if (!active) {
            return undefined;
        }
        const { id } = active as FabricObject & { id?: string };
        return id;
    }

    getActiveObjectAnchors():
        | { mb: { x: number; y: number }; ml: { x: number; y: number }; mr: { x: number; y: number }; mt: { x: number; y: number } }
        | undefined {
        const active = this._canvas.getActiveObjects()[0];
        if (!active) {
            return undefined;
        }
        const { mb, ml, mr, mt } = active.oCoords;

        return {
            mb: { x: mb.x, y: mb.y },
            ml: { x: ml.x, y: ml.y },
            mr: { x: mr.x, y: mr.y },
            mt: { x: mt.x, y: mt.y },
        };
    }

    getCanvasClientRect(): { height: number; width: number } {
        const rect = this._canvas.getElement().getBoundingClientRect();
        return { height: rect.height, width: rect.width };
    }

    getViewportTransform(): { scaleX: number; scaleY: number; translateX: number; translateY: number } {
        const vpt = this._canvas.viewportTransform as number[];
        return {
            scaleX: vpt[0],
            scaleY: vpt[3],
            translateX: vpt[4],
            translateY: vpt[5],
        };
    }

    isEditingActiveObject(): boolean {
        const active = this._canvas.getActiveObject();
        if (!active) {
            return false;
        }
        return 'isEditing' in active && Boolean((active as { isEditing?: boolean }).isEditing);
    }
}

export { FabricSceneQueryService };
